import { message } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type CtsModuleSpecsFragment } from '../../../api/fragments/CtsModuleSpecs';
import { type ModuleListDataFragment } from '../../../api/fragments/ModuleListData';
import { useUpdateCtsFinanceProductsMutation } from '../../../api/mutations/updateCtsFinanceProducts';
import { useUpdateCtsInsuranceProductsMutation } from '../../../api/mutations/updateCtsInsuranceProducts';
import { useGetModuleSpecsQuery } from '../../../api/queries/getModuleSpecs';
import Form from '../../../components/fields/Form';
import useHandleError from '../../../utilities/useHandleError';
import CtsManagementSetting, { type FormValues } from './CtsManagementSetting';

type CtsManagementProps = {
    module: ModuleListDataFragment;
};

type CtsManagementInnerProps = {
    module: CtsModuleSpecsFragment;
};

const CtsManagementInner = ({ module }: CtsManagementInnerProps) => {
    const { t } = useTranslation('ctsDetails');

    const [financeMutation] = useUpdateCtsFinanceProductsMutation();
    const [insuranceMutation] = useUpdateCtsInsuranceProductsMutation();

    const initialValues = useMemo<FormValues>(
        () => ({
            settings:
                module.ctsSettings?.map(item => ({
                    id: item.id,
                    financeProductSuiteIds: item.financeProductSuiteIds,
                    insuranceProductSuiteIds: item.insuranceProductSuiteIds,
                })) ?? [],
        }),
        [module.ctsSettings]
    );

    const onSubmit = useHandleError<FormValues>(
        async values => {
            const { settings } = values;

            message.loading({
                content: t('ctsDetails:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // for each of the settings, call API mutation
            /* eslint-disable no-await-in-loop */
            for (const setting of settings) {
                const { financeProductSuiteIds, insuranceProductSuiteIds } = setting;

                await financeMutation({
                    variables: { moduleId: module.id, financeProductSuiteIds, settingId: setting.id },
                });

                if (insuranceProductSuiteIds) {
                    await insuranceMutation({
                        variables: { moduleId: module.id, insuranceProductSuiteIds, settingId: setting.id },
                    });
                }
            }
            /* eslint-enable no-await-in-loop */
            message.destroy('primary');
            message.success({
                content: t('ctsDetails:messages.updateSuccessful'),
                key: 'primary',
            });
        },
        [financeMutation, insuranceMutation, module.id, t]
    );

    const formId = `ctsDetails-${module.id}`;

    return (
        <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit}>
            {({ handleSubmit }) => (
                <Form id={formId} name={formId} onSubmitCapture={handleSubmit}>
                    <CtsManagementSetting module={module} name="settings" />
                </Form>
            )}
        </Formik>
    );
};

const CtsManagement = ({ module }: CtsManagementProps) => {
    const { data, loading } = useGetModuleSpecsQuery({
        variables: {
            moduleId: module?.id,
        },

        skip: !module?.id,
    });

    const detailModule = useMemo(() => (data?.module?.__typename === 'CtsModule' ? data.module : null), [data]);

    if (loading || !detailModule) {
        return null;
    }

    return <CtsManagementInner module={detailModule} />;
};

export default CtsManagement;
